/**
 * Scroll decision helpers for the SMS thread in Messages.js.
 *
 * The thread used to call `bottomRef.current.scrollIntoView()` in an effect
 * keyed on `threadMessages`. That array gets a fresh identity on every 4s
 * poll (`setMessages(data)`), so the view snapped back to the bottom even
 * while the user was reading a long message or mid-gesture. These helpers
 * make the decision explicit and testable: content identity, not array
 * identity, drives autoscroll.
 */

/** Distance from the bottom (px) still counted as "at the bottom". */
export const NEAR_BOTTOM_PX = 80;

/**
 * True when the scroll container is scrolled to (or very near) the bottom.
 * Placeholder elements / jsdom without layout report 0 for every metric,
 * which reads as "at the bottom" — the safe default.
 */
export function isNearBottom(el, threshold = NEAR_BOTTOM_PX) {
  if (!el) return true;
  const scrollHeight = el.scrollHeight || 0;
  const scrollTop = el.scrollTop || 0;
  const clientHeight = el.clientHeight || 0;
  return scrollHeight - scrollTop - clientHeight <= threshold;
}

/**
 * Stable identity for one message. Falls back through the fields the backend
 * actually sends so a message keeps the same key across poll refreshes
 * (`sid`), optimistic sends (temp key replaced by the server `sid` is a
 * *different* key, but the count stays the same so it is not treated as a
 * "new message" — see shouldAutoScroll), and translation re-renders
 * (body changes, key does not).
 */
export function messageKey(m, index = 0) {
  if (!m) return `idx:${index}`;
  if (m.sid) return `sid:${m.sid}`;
  if (m.id != null) return `id:${m.id}`;
  if (m.timestamp) return `ts:${m.timestamp}|${m.direction || ''}|${m.body || ''}`;
  return `idx:${index}`;
}

/**
 * Newest-message key + count for a thread — the "last seen" snapshot the
 * component keeps in a ref so it can tell a real arrival from a poll echo.
 */
export function threadSnapshot(threadMessages) {
  const list = threadMessages || [];
  if (list.length === 0) return { key: '', count: 0 };
  return { key: messageKey(list[list.length - 1], list.length - 1), count: list.length };
}

/**
 * Decide whether a thread render should move the scroll container.
 *
 * @param {object}   args
 * @param {string}   args.threadId      active contact; a change means "new thread"
 * @param {string}   args.lastSeenKey   newest message key rendered last time (ref)
 * @param {number}   args.lastSeenCount message count rendered last time (ref)
 * @param {string}   args.newestKey     newest message key in this render
 * @param {number}   args.count         message count in this render
 * @param {boolean}  args.nearBottom    isNearBottom(container) on this render
 * @param {boolean}  [args.force]       user just sent / switched / tapped the pill
 * @returns {{scroll: boolean, showPill: boolean, reason: string}}
 *   scroll    — jump the container to the bottom
 *   showPill  — "New message ↓" affordance belongs above the composer
 */
export function shouldAutoScroll({
  threadId = null,
  lastSeenKey = '',
  lastSeenCount = 0,
  newestKey = '',
  count = 0,
  nearBottom = true,
  force = false,
} = {}) {
  // Nothing rendered (conversation list, or thread not resolved yet).
  if (!threadId) {
    return { scroll: false, showPill: false, reason: 'no-thread' };
  }
  if (force) {
    return { scroll: true, showPill: false, reason: 'forced' };
  }
  // A thread with no previously-seen snapshot is either freshly opened or
  // switched into: land at the bottom, instantly.
  if (!lastSeenKey && lastSeenCount === 0) {
    return { scroll: true, showPill: false, reason: 'thread-opened' };
  }

  const grew = count > lastSeenCount;
  // A *new* arrival increases the count. A key change at the same count is the
  // server replacing an optimistic bubble with its real sid (or a translation
  // update) — same conversation, so it must never move the view or raise the
  // pill. Only a count increase is treated as genuinely new.
  const genuinelyNew = grew;

  if (!genuinelyNew) {
    // Identical poll refresh (or a translation update): never move.
    return { scroll: false, showPill: false, reason: 'no-new-message' };
  }
  if (nearBottom) {
    return { scroll: true, showPill: false, reason: 'new-message-near-bottom' };
  }
  return { scroll: false, showPill: true, reason: 'new-message-scrolled-up' };
}
